import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import type { TicketStatusRef } from '@central/contracts';
import { Button } from '@/components/Button';
import { mensagemDeErro } from '@/lib/format';
import { apenasAtivos, situacaoPorId, useOpcoesDeFormulario } from '@/features/tickets/opcoes';
import { chaves, mudarSituacao, verificarFechamento } from '@/features/tickets/tickets.api';
import type { CategoriaDeStatus, Chamado } from '@/features/tickets/types';

/**
 * Mudança de situação do chamado.
 *
 * As situações válidas são dado do tenant, não uma lista no código: vêm de
 * `/lookups/ticket-form`, e o que o servidor recebe é a **chave**, não o nome
 * nem a categoria.
 *
 * ## Por que o veredicto de fechamento entra aqui
 *
 * Fechar um pai com filho aberto é recusado pelo servidor com 400. Deixar o
 * operador escolher, escrever a nota, clicar e só então descobrir isso é
 * transformar uma regra conhecida em erro de formulário. `GET /can-close`
 * existe exatamente para a tela saber antes — então as situações de
 * encerramento aparecem desabilitadas, com o motivo que a API escreveu à
 * vista.
 */

/**
 * As categorias que o servidor trata como encerramento.
 *
 * Espelha `DONE_CATEGORIES` do `TicketsService`: são estas duas que disparam a
 * conferência de `canClose` e a permissão `ticket.close`. `CANCELLED` não
 * entra — cancelar não é fechar, e o servidor não o barra por filho aberto.
 */
const CATEGORIAS_DE_ENCERRAMENTO = new Set<CategoriaDeStatus>(['RESOLVED', 'CLOSED']);

export function ControleDeSituacao({ chamado }: { chamado: Chamado }) {
  const clienteDeConsulta = useQueryClient();
  const opcoes = useOpcoesDeFormulario();

  // Mesma chave que o painel de subchamados usa: o veredicto é buscado uma vez
  // e serve às duas seções.
  const fechamento = useQuery({
    queryKey: chaves.fechamento(chamado.id),
    queryFn: () => verificarFechamento(chamado.id),
  });

  const [escolhida, setEscolhida] = useState('');
  const [nota, setNota] = useState('');

  const atual = situacaoPorId(opcoes.data, chamado.statusId);
  const disponiveis = apenasAtivos(opcoes.data?.statuses ?? []);
  const selecionada = escolhida || atual?.key || '';
  const destino = disponiveis.find((situacao) => situacao.key === selecionada) ?? null;

  const jaEncerrado = CATEGORIAS_DE_ENCERRAMENTO.has(chamado.statusCategory);
  const impedido = fechamento.data ? !fechamento.data.allowed : false;

  /**
   * Um destino está bloqueado quando encerraria o chamado agora e os filhos
   * ainda não terminaram. Reabrir e cancelar passam: a regra é sobre encerrar.
   */
  const bloqueada = (situacao: TicketStatusRef): boolean =>
    impedido && !jaEncerrado && CATEGORIAS_DE_ENCERRAMENTO.has(situacao.category);

  const encerrandoAgora =
    destino !== null && !jaEncerrado && CATEGORIAS_DE_ENCERRAMENTO.has(destino.category);

  const aplicar = useMutation<Chamado, Error, void>({
    mutationFn: () =>
      mudarSituacao(chamado.id, {
        statusKey: selecionada,
        ...(nota.trim() ? { note: nota.trim() } : {}),
      }),
    onSuccess: () => {
      setEscolhida('');
      setNota('');
      // A mudança propaga para o pai, para a árvore e para os relógios de SLA;
      // invalidar a raiz é mais honesto do que enumerar chaves e esquecer uma.
      void clienteDeConsulta.invalidateQueries({ queryKey: chaves.raiz });
    },
  });

  if (opcoes.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {mensagemDeErro(opcoes.error) ?? 'Não foi possível carregar as situações.'}
      </p>
    );
  }

  const semMudanca = selecionada === '' || selecionada === atual?.key;

  return (
    <div className="space-y-2">
      <label htmlFor="situacao-do-chamado" className="text-sm font-medium">
        Situação
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <select
          id="situacao-do-chamado"
          value={selecionada}
          disabled={opcoes.isPending || disponiveis.length === 0}
          onChange={(evento) => setEscolhida(evento.target.value)}
          className="min-w-[12rem] rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow disabled:opacity-50"
        >
          {/* Enquanto as opções não chegam não há como dizer em que situação o
              chamado está: um campo em branco seria mais honesto que um nome
              chutado, e um rótulo explícito é melhor que os dois. */}
          {selecionada === '' ? (
            <option value="">{opcoes.isPending ? 'Carregando…' : 'Situação desconhecida'}</option>
          ) : null}

          {/* A situação atual pode estar desativada — chamado antigo parado num
              status que saiu de operação. Ela entra na lista para o campo não
              começar mostrando outra coisa, mas fica fora das escolhas novas. */}
          {atual && !disponiveis.some((situacao) => situacao.key === atual.key) ? (
            <option value={atual.key}>{atual.name} (situação atual, desativada)</option>
          ) : null}

          {disponiveis.map((situacao) => (
            <option key={situacao.id} value={situacao.key} disabled={bloqueada(situacao)}>
              {situacao.name}
              {bloqueada(situacao) ? ' — indisponível' : ''}
            </option>
          ))}
        </select>

        <Button
          size="sm"
          loading={aplicar.isPending}
          disabled={semMudanca || (destino !== null && bloqueada(destino))}
          onClick={() => aplicar.mutate()}
        >
          Aplicar
        </Button>
      </div>

      {encerrandoAgora ? (
        <input
          value={nota}
          onChange={(evento) => setNota(evento.target.value)}
          aria-label="Nota de resolução"
          placeholder="Nota de resolução (opcional)"
          className="w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm transition-colors placeholder:text-muted-foreground hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow"
        />
      ) : null}

      {impedido && !jaEncerrado ? (
        <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
          <span>{fechamento.data?.reason ?? 'Este chamado ainda não pode ser encerrado.'}</span>
        </p>
      ) : null}

      {aplicar.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDeErro(aplicar.error)}
        </p>
      ) : null}
    </div>
  );
}
